import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { IconMessage } from '@/components/IconMessage/IconMessage';
import Loader from '@/components/Loaders/Loader';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import ShareMenu from '@/components/ShareMenu/ShareMenu';
import { useDownloadFileMutation } from '@/hooks/useDownloadFileMutation';
import CopyLink from '@/images/CopyLink.svg';
import ErrorIcon from '@/images/DangerCircle.svg';
import Remove from '@/images/Remove.svg';
import Share from '@/images/Share.svg';
import Subtitles from '@/images/Subtitles.svg';
import Download from '@/images/WhiteDownload.svg';
import PlayModalLanguageSelect from '@/pages/translation/components/PlayModal/PlayModalLanguageSelect';
import {
    getFailedTranslationsLanguages,
    getIdToTranslationMap,
    translationsArrayIntoObject,
} from '@/pages/translation/components/PlayModal/utils';
import { useAnalytics } from '@/services/analytics';
import { createShareRecord } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { useGetTranslations } from '@/state/translations';
import theme from '@/theme';
import { copyToClipboardAsShareUrl, createShareUrl, sendShareMixpanelEvent, transformToShareItem } from '@/utils/shareUtils';
import { StyleCollection } from '@/utils/types';
import { capitalizeLanguages, isMobile } from '@/utils/utils';
import { ITranslation, LibraryItem, TranslationStatus } from '@d-id/types';
import { Theme, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogTitle } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { ORIGINAL_TRANSLATION_ID } from '../../constants';
import { translationToDelete } from '../../state';
import { transformToLibraryItem } from '../../utils';
import { EXAMPLE_TRANSLATIONS } from '../TranslationLibrary/example-translations';
import { isOriginalTranslation } from './utils';

interface Props {
    libraryItem: LibraryItem;
    isOpen: boolean;
    handleClose: () => void;
    onPlay?: () => void;
    isExample?: boolean;
}

const VideoTitle = styled.div`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 2rem;
    min-height: 2rem;
    font-weight: 700;
    letter-spacing: -0.32px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const StyledCustomTooltip = muiStyled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} placement="top-start" />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#000000',
        color: theme.colors.white,
        fontSize: theme.typography.pxToRem(24),
        borderRadius: '4px',
    },
}));

const DownloadButton = styled(Button)<{ primary: boolean; isOnMobile?: boolean }>`
    width: auto;
    height: 38px;
    color: #ffffff;
    background-color: #000000;

    &:hover {
        background: ${theme.colors.secondary[800]} !important;
    }

    &.MuiButton-contained:hover {
        background: ${theme.colors.secondary[800]} !important;
    }

    ${props =>
        props.isOnMobile
            ? `
        width: 4rem !important;
        padding: 0 0.7rem 0 2rem;
        min-width: 0;
        `
            : ''}
    ${[theme.breakpoints.down(780)]}: {
        width: 45%;
    }
`;

const SecondaryActionButton = styled(Button)<{ show?: boolean; isOnMobile?: boolean }>`
    display: ${({ show }) => (show !== false ? 'flex' : 'none')};
    margin-left: auto;
    height: 38px;
    ${props =>
        props.isOnMobile
            ? `
        width: 4rem !important;
        padding: 0 0.9rem 0 2rem;
        min-width: 0;
        `
            : 'min-width: 10rem;'}
    ${[theme.breakpoints.down(780)]}: {
        display: none;
    }
`;

const ActionsGroupContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const StyledDialog = styled(BootstrapDialog)`
    & .MuiPaper-root {
        padding: 20px 38px;
        box-sizing: content-box;
        gap: 12px;
        max-width: 1024px;
        max-height: 720px;
        border-radius: 20px;
        height: 90%;
        overflow-y: unset;

        @media (max-height: 780px) {
            margin: 120px 0;
            background: #f1f2f6;
        }
    }

    & .MuiDialogContent-root {
        flex: 1;
        padding: 0;
        width: 100%;
        gap: 40px;

        @media (max-height: 780px) {
            max-height: 80%;
        }
    }
`;

export const styles: StyleCollection = {
    svgFill: {
        height: '100%',
        width: '100%',
    },

    actions: {
        gap: '1.8rem',
        padding: '0 0 1rem 0 !important',
        display: 'flex',
        justifyContent: 'space-between !important',
        width: '100%',
    },
    closeIcon: {
        marginLeft: 'auto',
        height: '4.2rem',
        width: '4.2rem',
        color: (theme: Theme) => theme.colors.primary,
    },
    titlestyle: {
        padding: '0',
    },
};

const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;

const StyledVideo = styled.video`
    height: calc(100% - 186px);
    margin: auto 0;

    ::cue {
        text-shadow:
            -1px -1px 0 #000,
            1px -1px 0 #000,
            -1px 1px 0 #000,
            1px 1px 0 #000;
    }

    &:focus {
        outline: none;
    }
`;

const mixpanelFrom = 'translate-play-modal';

export const PlayModal = ({ libraryItem, isOpen, handleClose, onPlay, isExample }: Props) => {
    const mixpanel = useAnalytics();
    const setTranslationToDelete = useSetRecoilState(translationToDelete);
    const [selectedTranslationId, setSelectedTranslationId] = useState<string | undefined>(undefined);
    const originalTranslationId = `${ORIGINAL_TRANSLATION_ID}_${libraryItem.id}`;
    const [isLoading, setIsLoading] = useState(true);

    const videoRef = useRef<HTMLVideoElement>(null);
    const isOnMobile = isMobile();
    const { isAnonymous } = useAuth();
    const { data } = useGetTranslations(isAnonymous, libraryItem.id, isOpen && !isExample);
    const playSubtitlesIsEnabled = useFlag('play_subtitles_is_enabled', false);
    const theme = useTheme();
    const [shareAnchorEl, setShareAnchorEl] = useState<HTMLButtonElement | null>(null);
    const { mutateAsync: download } = useDownloadFileMutation(() =>
        mixpanel?.track('translate-download', {
            event: 'cancel',
            from: mixpanelFrom,
            id: selectedTranslationId,
        })
    );
    const idToTranslation = useMemo(() => {
        const translations = isExample ? EXAMPLE_TRANSLATIONS.filter(t => t.group_id === libraryItem.id) : data?.translations;

        return translationsArrayIntoObject(translations);
    }, [data]);
    const idTranslationMap = useMemo(
        () => getIdToTranslationMap({ idToTranslation, libraryItem, originalTranslationId }),
        [idToTranslation]
    );
    const failedTranslationsLanguages = useMemo(() => getFailedTranslationsLanguages(idTranslationMap), [idTranslationMap]);

    useEffect(() => {
        if (!isOpen || !idTranslationMap || !Object.keys(idTranslationMap).length) {
            return;
        }

        if (isExample) {
            return setSelectedTranslationId(originalTranslationId);
        }

        if (selectedTranslationId && idTranslationMap[selectedTranslationId]) {
            return;
        }

        const sortedTranslations = Object.values(idTranslationMap)
            .filter(translation => translation.status === TranslationStatus.Done && !isOriginalTranslation(translation.id))
            .sort((a, b) => {
                return a.language.localeCompare(b.language);
            });
        setSelectedTranslationId(sortedTranslations[0]?.id);
    }, [idTranslationMap, isOpen]);

    const videoName = useMemo(() => {
        if (isOpen && selectedTranslationId && idTranslationMap[selectedTranslationId]) {
            return `${libraryItem.name}_${idTranslationMap[selectedTranslationId].language}`;
        }
    }, [selectedTranslationId, isOpen]);

    useEffect(() => {
        if (isOpen && videoRef.current) {
            !videoRef.current.paused && videoRef.current.pause();
            videoRef.current.load();
            videoRef.current.play().catch(error => {
                console.error('Failed to play the new video:', error);
            });
        }
    }, [selectedTranslationId, isOpen]);

    const isSingleGroup = isOpen && idTranslationMap && Object.keys(idTranslationMap).length > 1;

    const handleRemove = () => {
        mixpanel?.track('translate-delete-video', { event: 'click', from: mixpanelFrom, id: selectedTranslationId });
        selectedTranslationId && idTranslationMap[selectedTranslationId] && setTranslationToDelete(idTranslationMap[selectedTranslationId]);

        handleClose();
    };

    const handleDownloadTranslation = async () => {
        mixpanel?.track('translate-download', { event: 'click', from: mixpanelFrom, id: selectedTranslationId });
        if (selectedTranslationId && idTranslationMap[selectedTranslationId]) {
            await download({
                dataurl: idTranslationMap[selectedTranslationId].result_url!,
                filename: videoName ?? 'video',
                extension: '.mp4',
            });
        }
    };

    const handleDownloadSubtitles = async () => {
        if (selectedTranslationId && idTranslationMap[selectedTranslationId]) {
            await download({
                dataurl: idTranslationMap[selectedTranslationId].subtitles_url!,
                filename: videoName ?? 'video',
                extension: '.srt',
            });
        }
        mixpanel?.track('export-subtitles', { from: 'translate-preview', id: selectedTranslationId });
    };

    const handleVideoLoaded = (event: any) => {
        setIsLoading(false);
        onPlay?.();
        const target = event.target as HTMLVideoElement;
        target.currentTime = 0;
        target.controls = true;
        target.play().catch(e => {
            console.error('Failed to play video', e);
        });
    };

    return (
        <>
            <StyledDialog onClose={handleClose} width="80%" contentstyle={styles.contentstyle} titlestyle={styles.titlestyle} open={isOpen}>
                {isLoading && (
                    <LoaderContainer>
                        <Loader />
                    </LoaderContainer>
                )}
                <DialogTitle>
                    <VideoTitle>{libraryItem?.name ?? ''}</VideoTitle>
                    <IconButton ariaLabel="close" onClick={handleClose} sx={styles.closeIcon}>
                        <CloseIcon sx={styles.svgFill} />
                    </IconButton>
                </DialogTitle>

                {failedTranslationsLanguages.length > 0 && (
                    <StyledCustomTooltip
                        title={<div>Failed to translate to: {capitalizeLanguages(failedTranslationsLanguages).join(',')}</div>}>
                        <div>
                            <IconMessage icon={ErrorIcon} iconColor={theme.colors.error.main}>
                                {failedTranslationsLanguages.length === 1
                                    ? `Failed to translate to: ${capitalizeLanguages(failedTranslationsLanguages).join(',')}`
                                    : 'Some languages failed to translate'}
                            </IconMessage>
                        </div>
                    </StyledCustomTooltip>
                )}

                {isSingleGroup && (
                    <PlayModalLanguageSelect
                        idTranslationsMap={idTranslationMap}
                        setSelectedTranslationId={setSelectedTranslationId}
                        selectedTranslationId={selectedTranslationId}
                    />
                )}
                {selectedTranslationId && idTranslationMap[selectedTranslationId] && (
                    <StyledVideo
                        ref={videoRef}
                        preload="auto"
                        onLoadedMetadata={handleVideoLoaded}
                        poster={idTranslationMap[selectedTranslationId].thumbnail_url}
                        controls={false}
                        controlsList="nodownload"
                        crossOrigin="use-credentials"
                        playsInline>
                        <source src={idTranslationMap[selectedTranslationId].result_url} type="video/mp4" />
                        Your browser does not support the video tag.
                        {playSubtitlesIsEnabled && !isOriginalTranslation(selectedTranslationId) && (
                            <track
                                kind="subtitles"
                                src={idTranslationMap[selectedTranslationId].subtitles_url?.replace('.srt', '.vtt')}
                                default
                            />
                        )}
                    </StyledVideo>
                )}
                {!isExample && selectedTranslationId && idTranslationMap[selectedTranslationId] && (
                    <DialogActions sx={styles.actions}>
                        <ActionsGroupContainer>
                            <Button
                                variant="text"
                                color="secondary"
                                startIcon={<img src={CopyLink} />}
                                onClick={() => {
                                    createShareRecord(transformToLibraryItem(idTranslationMap[selectedTranslationId]));
                                    copyToClipboardAsShareUrl({ itemId: selectedTranslationId });
                                    sendShareMixpanelEvent({
                                        item: transformToShareItem<ITranslation>(idTranslationMap[selectedTranslationId], 'video'),
                                        modal: 'play',
                                        to: 'copy',
                                        from: 'translate-preview',
                                    });
                                }}>
                                Copy link
                            </Button>

                            <SecondaryActionButton
                                show
                                variant="text"
                                color="secondary"
                                disabled={
                                    isOriginalTranslation(selectedTranslationId) || !idTranslationMap[selectedTranslationId].subtitles_url
                                }
                                onClick={handleDownloadSubtitles}
                                startIcon={<img src={Subtitles} />}
                                isOnMobile={isOnMobile}>
                                {!isOnMobile && 'Download subtitles (.srt)'}
                            </SecondaryActionButton>
                            <SecondaryActionButton
                                variant="text"
                                color="error"
                                disabled={isOriginalTranslation(selectedTranslationId)}
                                onClick={handleRemove}
                                startIcon={<MaskedIcon src={Remove} color={theme.colors.error.main} size="24px" />}
                                isOnMobile={isOnMobile}>
                                {!isOnMobile && 'Delete'}
                            </SecondaryActionButton>
                        </ActionsGroupContainer>
                        <ActionsGroupContainer>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<img src={Share} />}
                                onClick={e => {
                                    setShareAnchorEl(e.currentTarget);
                                }}>
                                Share
                            </Button>
                            <DownloadButton
                                primary
                                variant="contained"
                                onClick={handleDownloadTranslation}
                                startIcon={<img src={Download} />}
                                isOnMobile={isOnMobile}>
                                {!isOnMobile && 'Download'}
                            </DownloadButton>
                        </ActionsGroupContainer>
                    </DialogActions>
                )}
                {selectedTranslationId && idTranslationMap[selectedTranslationId] && (
                    <ShareMenu
                        createShareUrl={(option: string) =>
                            createShareUrl({
                                itemId: selectedTranslationId,
                                from: option,
                                type: 'video',
                            })
                        }
                        handleShare={(option: string) => {
                            createShareRecord(transformToLibraryItem(idTranslationMap[selectedTranslationId]));

                            sendShareMixpanelEvent({
                                item: transformToShareItem<ITranslation>(idTranslationMap[selectedTranslationId], 'video'),
                                modal: 'play',
                                to: option,
                                from: 'translate-preview',
                            });
                        }}
                        open={!!shareAnchorEl}
                        anchorEl={shareAnchorEl}
                        anchorOrigin={{ vertical: -20, horizontal: 'center' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        onClose={() => {
                            setShareAnchorEl(null);
                        }}
                    />
                )}
            </StyledDialog>
        </>
    );
};
